import { useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import { customInstance } from "config/mutator";

import { userLayoutKeys } from "./query-keys";

/**
 * Per-table layout from GET /api/users/layout.
 * Top-level keys are table ids (snake_case, e.g. campaigns, approval_queue).
 * Additional properties besides `hidden` may be added by the API over time.
 */
export type UserTableLayoutSettings = {
  hidden?: string[];
  [key: string]: unknown;
};

export type UserTableLayout = Record<string, UserTableLayoutSettings>;

const fetchUserLayout = (): Promise<UserTableLayout> =>
  customInstance<UserTableLayout>({ url: "/api/users/layout", method: "GET" });

export const useUserLayout = <TData = UserTableLayout>(
  options?: Omit<UseQueryOptions<UserTableLayout, Error, TData>, "queryKey" | "queryFn">,
) => {
  return useQuery({
    queryKey: userLayoutKeys.current(),
    queryFn: fetchUserLayout,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

const patchUserLayout = (payload: UserTableLayout): Promise<void> =>
  customInstance({ url: "/api/users/layout", method: "PATCH", data: payload });

export const useUpdateUserLayout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchUserLayout,
    // Mark stale without auto-refetching, so an in-flight optimistic edit
    // is not overwritten by a server GET response that lags behind the user.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: userLayoutKeys.current(),
        refetchType: "none",
      });
    },
  });
};

export const useDebouncedUpdateUserLayout = (delay = 300) => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useUpdateUserLayout();
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rollbackSnapshotRef = useRef<UserTableLayout | undefined>(undefined);

  const captureSnapshot = useCallback(() => {
    if (rollbackSnapshotRef.current === undefined) {
      rollbackSnapshotRef.current =
        queryClient.getQueryData<UserTableLayout>(userLayoutKeys.current()) ?? {};
    }
  }, [queryClient]);

  const scheduleFlush = useCallback(() => {
    // Fire-and-forget: cancel any in-flight GET so a stale response cannot
    // overwrite the freshly written optimistic cache.
    queryClient.cancelQueries({ queryKey: userLayoutKeys.current() });

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const merged =
        queryClient.getQueryData<UserTableLayout>(userLayoutKeys.current()) ?? {};
      const snapshot = rollbackSnapshotRef.current;
      rollbackSnapshotRef.current = undefined;

      mutate(merged, {
        onError: () => {
          if (snapshot !== undefined) {
            queryClient.setQueryData(userLayoutKeys.current(), snapshot);
          }
        },
      });
    }, delay);
  }, [queryClient, mutate, delay]);

  /**
   * Synchronous optimistic update.
   * Writes to the cache via a functional updater so that rapid consecutive calls
   * always derive `next` from the freshest state (no stale-closure / await race).
   */
  const update = useCallback(
    (payload: UserTableLayout) => {
      captureSnapshot();
      queryClient.setQueryData<UserTableLayout>(userLayoutKeys.current(), (old = {}) => ({
        ...old,
        ...Object.fromEntries(
          Object.entries(payload).map(([key, value]) => [key, { ...old?.[key], ...value }]),
        ),
      }));
      scheduleFlush();
    },
    [queryClient, captureSnapshot, scheduleFlush],
  );

  /**
   * Toggle a single column's visibility for the given table.
   * The next `hidden` is derived inside the functional `setQueryData`, so each
   * click reads the latest (already optimistically updated) cache and cannot
   * lose previous toggles when the user clicks faster than React can re-render.
   */
  const toggleColumnVisibility = useCallback(
    (tableKey: string, columnId: string) => {
      captureSnapshot();
      queryClient.setQueryData<UserTableLayout>(userLayoutKeys.current(), (old = {}) => {
        const hidden = old?.[tableKey]?.hidden ?? [];
        const nextHidden = hidden.includes(columnId)
          ? hidden.filter((id) => id !== columnId)
          : [...hidden, columnId];

        return {
          ...old,
          [tableKey]: { ...old?.[tableKey], hidden: nextHidden },
        };
      });
      scheduleFlush();
    },
    [queryClient, captureSnapshot, scheduleFlush],
  );

  return { update, toggleColumnVisibility, isPending };
};
